/**
 * Parse a Traccar WKT area string into a drawable/testable shape.
 * Traccar orders EVERY shape "lat lng" — CIRCLE (lat lng, radius_m), POLYGON ((lat lng, ...)), LINESTRING (lat lng, ...) —
 * the reverse of standard WKT. The server evaluator (server/src/rules/geofence-geometry.ts), Traccar itself, both mobile apps and
 * the GPSgate importer read it that way; this file used lng-first for polygons and lines until TASK-339, which mirrored every
 * web-drawn polygon for everything but the web map. Parsed coordinates are [lng, lat] for MapLibre.
 */
export function parseWKT(area) {
  if (!area) return null;

  const circleMatch = area.match(/CIRCLE\s*\(\s*([-\d.]+)\s+([-\d.]+)\s*,\s*([-\d.]+)\s*\)/i);
  if (circleMatch) {
    return {
      type: 'circle',
      center: [parseFloat(circleMatch[2]), parseFloat(circleMatch[1])], // [lng, lat]
      radius: parseFloat(circleMatch[3]),
    };
  }

  const polyMatch = area.match(/POLYGON\s*\(\((.*?)\)\)/i);
  if (polyMatch) {
    const coords = polyMatch[1].split(',').map((pair) => {
      const [lat, lon] = pair.trim().split(/\s+/).map(Number);
      return [lon, lat]; // [lng, lat]
    });
    return { type: 'polygon', coordinates: coords };
  }

  const lineMatch = area.match(/LINESTRING\s*\((.*?)\)/i);
  if (lineMatch) {
    const coords = lineMatch[1].split(',').map((pair) => {
      const [lat, lon] = pair.trim().split(/\s+/).map(Number);
      return [lon, lat];
    });
    return { type: 'linestring', coordinates: coords };
  }
  return null;
}

/** Convert a drawn shape ([lng, lat] coordinates) to Traccar WKT — lat first for every shape. */
export function toWKT(shape) {
  if (shape.type === 'circle') {
    const [lng, lat] = shape.center;
    return `CIRCLE (${lat} ${lng}, ${shape.radius})`;
  }
  if (shape.type === 'polygon') {
    return `POLYGON ((${shape.coordinates.map(([lng, lat]) => `${lat} ${lng}`).join(', ')}))`;
  }
  if (shape.type === 'linestring') {
    return `LINESTRING (${shape.coordinates.map(([lng, lat]) => `${lat} ${lng}`).join(', ')})`;
  }
  return '';
}

function haversineMeters(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

/** Ray-casting point-in-polygon over [lng, lat] rings. */
function pointInPolygon(lat, lng, ring) {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i]; // [lng, lat]
    const [xj, yj] = ring[j];
    const intersect = (yi > lat) !== (yj > lat)
      && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

/** Is (lat,lng) inside a parsed geofence shape? Linestrings are never "inside". */
export function pointInGeofence(lat, lng, parsed) {
  if (!parsed) return false;
  if (parsed.type === 'circle') {
    const [clng, clat] = parsed.center;
    return haversineMeters(lat, lng, clat, clng) <= parsed.radius;
  }
  if (parsed.type === 'polygon') {
    return pointInPolygon(lat, lng, parsed.coordinates);
  }
  return false;
}
